import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PostForm from '../components/PostForm';
import { createPost, fetchAllConstructionList } from '../api/postApi';
import { useAuth } from '../context/AuthContext';
import { applyPostFormFieldChange, buildPostPayload, createInitialPostForm, mapConstructionToPostForm } from '../utils/postForm';

function PostCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [form, setForm] = useState(() => {
    const prefillConstruction = location.state?.prefillConstruction;
    return prefillConstruction ? mapConstructionToPostForm(prefillConstruction) : createInitialPostForm();
  });
  const [submitting, setSubmitting] = useState(false);
  const [isConstructionPopupOpen, setIsConstructionPopupOpen] = useState(false);
  const [constructionLoading, setConstructionLoading] = useState(false);
  const [constructionRows, setConstructionRows] = useState([]);
  const [constructionKeyword, setConstructionKeyword] = useState('');

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => applyPostFormFieldChange(prev, name, value));
  };

  const loadConstructions = useCallback(async (keyword = '') => {
    setConstructionLoading(true);
    try {
      const rows = await fetchAllConstructionList({
        possessor: keyword,
        sort: 'id,desc',
      });
      setConstructionRows(rows);
    } catch (error) {
      window.alert(`시공정보 조회 실패: ${error.message}`);
    } finally {
      setConstructionLoading(false);
    }
  }, []);

  const CONSTRUCTION_SEARCH_DEBOUNCE_MS = 300;

  useEffect(() => {
    if (!user?.name) return;

    setForm((prev) => {
      if (prev.receivedBy) return prev;
      return { ...prev, receivedBy: user.name };
    });
  }, [user?.name]);

  useEffect(() => {
    if (!isConstructionPopupOpen) return undefined;
    const timer = setTimeout(() => {
      void loadConstructions(constructionKeyword.trim());
    }, CONSTRUCTION_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [constructionKeyword, isConstructionPopupOpen, loadConstructions]);

  const onOpenConstructionPopup = () => {
    setConstructionKeyword('');
    setConstructionRows([]);
    setIsConstructionPopupOpen(true);
  };

  const onCloseConstructionPopup = () => {
    if (constructionLoading) return;
    setIsConstructionPopupOpen(false);
  };

  const onApplyConstruction = (construction) => {
    const shouldApply = window.confirm('선택한 시공정보를 AS 작성 폼에 불러오시겠습니까?');
    if (!shouldApply) return;

    setForm((prev) => {
      const mapped = mapConstructionToPostForm(construction);
      return {
        ...prev,
        ...mapped,
        receivedCompany: prev.receivedCompany,
        receivedBy: prev.receivedBy,
        receiptStatus: prev.receiptStatus,
        receivedAt: prev.receivedAt,
        issueDescription: prev.issueDescription,
        processedBy: prev.processedBy,
        processedAt: prev.processedAt,
        resolution: prev.resolution,
      };
    });
    setIsConstructionPopupOpen(false);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await createPost(buildPostPayload(form));
      window.alert('등록이 완료되었습니다.');
      navigate('/posts');
    } catch (error) {
      window.alert(`등록 실패: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const onBlockEnterSubmit = (event) => {
    if (event.key !== 'Enter') return;
    if (event.target?.tagName?.toLowerCase() === 'textarea') return;
    event.preventDefault();
  };

  return (
    <section className="page">
      <div className="page-header">
        <h2>AS 작성</h2>
        <button type="button" className="secondary" onClick={onOpenConstructionPopup}>
          시공 정보 불러오기
        </button>
      </div>
      <form onSubmit={onSubmit} onKeyDown={onBlockEnterSubmit}>
        <PostForm form={form} onChange={onChange} />
        <div className="button-row">
          <button type="button" className="secondary" onClick={() => navigate('/posts')}>
            목록으로
          </button>
          <button type="submit" disabled={submitting}>
            {submitting ? '저장 중...' : '저장'}
          </button>
        </div>
      </form>

      {isConstructionPopupOpen && (
        <div className="modal-backdrop" onClick={onCloseConstructionPopup}>
          <div className="upload-modal upload-modal--fixed-height" onClick={(event) => event.stopPropagation()}>
            <h3>시공 정보 불러오기</h3>

            <div className="filter-row">
              <input
                value={constructionKeyword}
                onChange={(event) => setConstructionKeyword(event.target.value)}
                placeholder="설비 소유자 검색 (입력 시 자동 검색)"
                autoComplete="off"
              />
            </div>

            <div className="table-wrapper fixed-list-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>설비 소유자</th>
                    <th>주소</th>
                    <th>선택</th>
                  </tr>
                </thead>
                <tbody>
                  {constructionRows.length === 0 ? (
                    <tr>
                      <td colSpan={3}>{constructionLoading ? '불러오는 중...' : '검색 결과가 없습니다.'}</td>
                    </tr>
                  ) : (
                    constructionRows.map((item) => (
                      <tr key={item.id}>
                        <td>{item.possessor || '-'}</td>
                        <td>{[item.address, item.detailAddress].filter(Boolean).join(' ') || '-'}</td>
                        <td>
                          <button type="button" className="secondary" onClick={() => onApplyConstruction(item)}>
                            불러오기
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="button-row modal-actions">
              <button type="button" className="danger" onClick={onCloseConstructionPopup} disabled={constructionLoading}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default PostCreatePage;
